// VoiceSettings: the voice engine's settings, all stored as data (design
// 4.1: "all stored as settings, none hard-coded"). The live protocol to the
// browser is vendor neutral; this config is where the vendor is named.
//
// Defaults are ported from today's consult (VERIFIED in code Sept 28 2026):
//   VAD "home":  ottos-consult api/_lib/gemini-vad.js VAD_DEFAULTS
//   VAD "room":  tradeshow-ottos-demo test/live-config.test.js (the booth values)
//   Mic gate:    ottos-consult src/consult/micGate.js DEFAULT_MIC_*
// The design proposes a 750 ms silence for launch, or 700 if test T7 passes
// (design 4.1); until T7 runs, home keeps today's proven 1000 ms.
import { z } from "zod";
import { DraftLabel } from "./common";

export const Sensitivity = z.enum(["HIGH", "LOW"]);

export const VadProfile = z.object({
  start_of_speech_sensitivity: Sensitivity,
  end_of_speech_sensitivity: Sensitivity,
  prefix_padding_ms: z.number().int().min(0).max(2000),
  silence_duration_ms: z.number().int().min(100).max(5000),
});
export type VadProfile = z.infer<typeof VadProfile>;

/** gemini-vad.js VAD_DEFAULTS: HIGH start (the Sept 14 fix for clipped soft starts), LOW end, 200 ms, 1000 ms. */
export const HOME_VAD: VadProfile = {
  start_of_speech_sensitivity: "HIGH",
  end_of_speech_sensitivity: "LOW",
  prefix_padding_ms: 200,
  silence_duration_ms: 1000,
};

/** The booth's values, for hosted and big-screen consults (design 4.1 "room"). */
export const ROOM_VAD: VadProfile = {
  start_of_speech_sensitivity: "LOW",
  end_of_speech_sensitivity: "LOW",
  prefix_padding_ms: 200,
  silence_duration_ms: 900,
};

export const MicGateMode = z.enum(["attenuate", "silence", "drop"]);
export type MicGateMode = z.infer<typeof MicGateMode>;

/** micGate.js defaults: the "home" profile that fixed clipped first syllables on Sept 14 2026. */
export const MicSettings = z.object({
  gate_mode: MicGateMode.default("attenuate"),
  gate_threshold_rms: z.number().min(0).max(1).default(0.005),
  gate_hangover_ms: z.number().int().min(0).max(5000).default(600),
  /** Gain for frames under the gate in attenuate mode. 0.25 is about minus 12 dB. */
  gate_floor_gain: z.number().min(0).max(1).default(0.25),
  gate_preroll_ms: z.number().int().min(0).max(1000).default(100),
  auto_gain_control: z.boolean().default(true),
  noise_suppression: z.boolean().default(true),
  echo_cancellation: z.boolean().default(true),
  /**
   * The echo guard (Oct 1 2026): while Otto plays, the mic must beat his own voice coming back (learned per device)
   * by this factor before it counts as the owner. 0 turns it off. 2.5 is a starting value (ESTIMATE).
   */
  echo_guard_margin: z.number().min(0).max(10).default(2.5),
  /** While Otto plays, the owner's voice must stay above the guard this long before the gate opens. */
  echo_guard_confirm_ms: z.number().int().min(0).max(500).default(60),
});
export type MicSettings = z.infer<typeof MicSettings>;

/**
 * Browser-side barge-in duck (design 4.3). Values are the design's starting
 * points for the lab (ESTIMATE), not measured. The duck threshold sits above
 * the mic gate so echo on laptop speakers does not duck Otto's own voice.
 */
export const DuckSettings = z.object({
  threshold_rms: z.number().min(0).max(1).default(0.02),
  hold_ms: z.number().int().min(0).max(2000).default(120),
  volume: z.number().min(0).max(1).default(0.3),
  confirm_timeout_ms: z.number().int().min(0).max(5000).default(600),
  fade_out_ms: z.number().int().min(0).max(500).default(30),
});
export type DuckSettings = z.infer<typeof DuckSettings>;

export const VoiceSettings = z.object({
  draft_label: DraftLabel,
  engine: z.enum(["gemini_live"]).default("gemini_live"),
  /** The VOICE_MODEL switch overrides this at run time (design 9.6). */
  model_id: z.string().min(1).default("gemini-3.1-flash-live-preview"),
  voice_name: z.string().min(1).default("Rasalgethi"),
  /** 3.1: "minimal" set explicitly. 3.8: must be omitted (design 4.1). */
  thinking_level: z.enum(["omit", "minimal", "low"]).default("minimal"),
  turn_coverage: z.enum(["TURN_INCLUDES_ONLY_ACTIVITY", "TURN_INCLUDES_ALL_INPUT"]).default("TURN_INCLUDES_ONLY_ACTIVITY"),
  input_transcription: z.boolean().default(true),
  output_transcription: z.boolean().default(true),
  /** The VAD_PROFILE switch picks one. "field" is UNKNOWN until test T32. */
  vad_profiles: z
    .object({
      home: VadProfile.default(HOME_VAD),
      room: VadProfile.default(ROOM_VAD),
      field: VadProfile.optional(),
    })
    .prefault({}),
  mic: MicSettings.prefault({}),
  duck: DuckSettings.prefault({}),
  /** Safety net inside every session (design 4.1): trigger 12k tokens, target 6k. */
  context_compression: z
    .object({
      trigger_tokens: z.number().int().min(1000).default(12_000),
      target_tokens: z.number().int().min(500).default(6_000),
    })
    .prefault({}),
});
export type VoiceSettings = z.infer<typeof VoiceSettings>;
